import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { TrendingUp, DollarSign, RotateCcw, Clock, CheckCircle, ArrowUpRight, ArrowDownRight, Minus, AlertTriangle, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;
function fmtAmt(v) { return v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`; }
const trendIcons = { growing: ArrowUpRight, stable: Minus, flat: Minus, slow: ArrowDownRight, new: Clock, blocked: AlertTriangle };
const trendColors = { growing: "text-[#22C55E]", stable: "text-blue-400", flat: "text-yellow-400", slow: "text-orange-400", new: "text-[#A1B2A8]/40", blocked: "text-red-400" };
function rotationColor(r) { return r >= 4 ? "text-[#22C55E]" : r >= 2 ? "text-blue-400" : r >= 1 ? "text-yellow-400" : r > 0 ? "text-orange-400" : "text-[#A1B2A8]/30"; }
function rotationLabel(r) { return r >= 5 ? "Excellent" : r >= 3 ? "Strong" : r >= 1.5 ? "Moderate" : r > 0 ? "Low" : "No Activity"; }

export default function AdminCapitalVelocity() {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("all");

  const fetchData = useCallback(async () => {
    try { const { data: d } = await axios.get(`${API}/admin/capital-velocity`, { withCredentials: true }); setData(d); }
    catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = filter === "all" ? data : data.filter(d => d.entity_type === filter);
  const activeParticipants = data.filter(d => d.total_trades > 0);
  const avgRotation = activeParticipants.length ? (activeParticipants.reduce((s, d) => s + d.rotation_rate, 0) / activeParticipants.length).toFixed(1) : "0";
  const totalVolume = data.reduce((s, d) => s + d.total_volume, 0);
  const totalCapital = data.reduce((s, d) => s + d.capital_deployed, 0);
  const platformRotation = totalCapital > 0 ? (totalVolume / totalCapital).toFixed(1) : "0";
  const avgSettlement = activeParticipants.length ? Math.round(activeParticipants.reduce((s, d) => s + d.avg_settlement_days, 0) / activeParticipants.length) : 0;

  return (
    <div data-testid="admin-capital-velocity">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-white">Capital Velocity</h1>
          <p className="text-[#A1B2A8]/60 text-sm">Fund rotation analysis — how fast capital moves through the network</p>
        </div>
        <div className="flex gap-2">
          {[["all", "All"], ["exporter", "Exporters"], ["buyer", "Buyers"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)}
              className={`text-xs px-3 py-1.5 border transition-colors ${filter === v ? "text-[#22C55E] border-[#22C55E]/50 bg-[#22C55E]/10" : "text-[#A1B2A8] border-[#1A2E22] hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Platform KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <div className="bg-[#09120D] border border-[#22C55E]/20 p-5">
          <div className="flex items-center gap-2 mb-2"><RotateCcw className="w-4 h-4 text-[#22C55E]" /><span className="text-[#A1B2A8] text-xs">Platform Rotation</span></div>
          <span className="font-['Outfit'] text-3xl font-bold text-[#22C55E]">{platformRotation}x</span>
          <p className="text-[#A1B2A8]/40 text-xs mt-1">{fmtAmt(totalVolume)} moved on {fmtAmt(totalCapital)} deployed</p>
        </div>
        <div className="bg-[#09120D] border border-[#1A2E22] p-5">
          <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-blue-400" /><span className="text-[#A1B2A8] text-xs">Avg Participant Rotation</span></div>
          <span className="font-['Outfit'] text-3xl font-bold text-blue-400">{avgRotation}x</span>
          <p className="text-[#A1B2A8]/40 text-xs mt-1">{activeParticipants.length} active participants</p>
        </div>
        <div className="bg-[#09120D] border border-[#1A2E22] p-5">
          <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-yellow-400" /><span className="text-[#A1B2A8] text-xs">Avg Settlement Cycle</span></div>
          <span className="font-['Outfit'] text-3xl font-bold text-yellow-400">{avgSettlement}d</span>
          <p className="text-[#A1B2A8]/40 text-xs mt-1">vs 60-90d traditional</p>
        </div>
        <div className="bg-[#09120D] border border-[#1A2E22] p-5">
          <div className="flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4 text-white" /><span className="text-[#A1B2A8] text-xs">Total Volume Moved</span></div>
          <span className="font-['Outfit'] text-3xl font-bold text-white">{fmtAmt(totalVolume)}</span>
          <p className="text-[#A1B2A8]/40 text-xs mt-1">{data.reduce((s, d) => s + d.total_trades, 0)} total trades</p>
        </div>
      </div>

      {/* Investor Insight Box */}
      <div className="bg-[#09120D] border border-[#22C55E]/20 p-6 mb-6">
        <div className="flex items-start gap-3">
          <RotateCcw className="w-5 h-5 text-[#22C55E] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[#22C55E] font-['Outfit'] font-semibold text-sm">Investor Insight: Capital Velocity Multiplier</p>
            <p className="text-[#A1B2A8] text-sm mt-1 leading-relaxed">
              Traditional food trade locks capital for 60-90 days per cycle. Delta GreenX infrastructure cuts this to {avgSettlement} days average — enabling {platformRotation}x capital rotation.
              That means every $1 deployed moves ${platformRotation} in trade volume annually. Top participants like Nordic Fjord achieve 5.4x rotation, turning $250K into $1.36M in executed trade.
            </p>
          </div>
        </div>
      </div>

      {/* Participant Table */}
      <div className="border border-[#1A2E22] overflow-x-auto">
        <table className="w-full" data-testid="velocity-table">
          <thead>
            <tr className="border-b border-[#1A2E22] bg-[#09120D]">
              <th className="text-left py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Participant</th>
              <th className="text-left py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Type</th>
              <th className="text-right py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Capital Deployed</th>
              <th className="text-right py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Volume Moved</th>
              <th className="text-center py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Rotation</th>
              <th className="text-center py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Settlement</th>
              <th className="text-center py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Trades</th>
              <th className="text-center py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Repeat</th>
              <th className="text-center py-3 px-4 text-[#A1B2A8] text-xs font-medium uppercase tracking-wider">Trend</th>
            </tr>
          </thead>
          <tbody>
            {filtered.sort((a, b) => b.rotation_rate - a.rotation_rate).map((v) => {
              const TrendIcon = trendIcons[v.trend] || Minus;
              return (
                <tr key={v.entity_id} className="border-b border-[#1A2E22] hover:bg-[#0D1B13]/50 transition-colors" data-testid={`vel-${v.entity_id}`}>
                  <td className="py-4 px-4">
                    <p className="text-white text-sm font-medium">{v.entity_name}</p>
                    {v.quarters_active > 0 && <p className="text-[#A1B2A8]/40 text-xs">{v.quarters_active}Q active</p>}
                  </td>
                  <td className="py-4 px-4">
                    <span className={`text-xs px-2 py-0.5 border capitalize ${v.entity_type === "exporter" ? "text-purple-400 border-purple-400/30 bg-purple-400/10" : "text-blue-400 border-blue-400/30 bg-blue-400/10"}`}>{v.entity_type}</span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="text-white text-sm font-mono">{v.capital_deployed > 0 ? fmtAmt(v.capital_deployed) : "—"}</span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="text-[#22C55E] text-sm font-mono font-semibold">{v.total_volume > 0 ? fmtAmt(v.total_volume) : "—"}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex flex-col items-center">
                      <span className={`font-['Outfit'] text-lg font-bold ${rotationColor(v.rotation_rate)}`}>{v.rotation_rate > 0 ? `${v.rotation_rate}x` : "—"}</span>
                      <span className={`text-[10px] ${rotationColor(v.rotation_rate)}`}>{rotationLabel(v.rotation_rate)}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    {v.avg_settlement_days > 0 ? (
                      <div>
                        <span className="text-white text-sm">{v.avg_settlement_days}d</span>
                        <p className="text-[#A1B2A8]/30 text-[10px]">{v.fastest_cycle}d–{v.slowest_cycle}d</p>
                      </div>
                    ) : <span className="text-[#A1B2A8]/30">—</span>}
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="text-white text-sm">{v.completed_trades}/{v.total_trades}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    {v.repeat_rate > 0 ? <span className={`text-sm ${v.repeat_rate >= 80 ? "text-[#22C55E]" : v.repeat_rate >= 50 ? "text-yellow-400" : "text-orange-400"}`}>{v.repeat_rate}%</span> : <span className="text-[#A1B2A8]/30">—</span>}
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <TrendIcon className={`w-3.5 h-3.5 ${trendColors[v.trend] || "text-[#A1B2A8]"}`} />
                      <span className={`text-xs capitalize ${trendColors[v.trend] || "text-[#A1B2A8]"}`}>{v.trend}</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Quarterly Breakdown for top performers */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.filter(v => v.quarterly && v.quarterly.length > 0 && v.rotation_rate > 0).slice(0, 4).map((v) => {
          const maxVol = Math.max(...v.quarterly.map(q => q.vol), 1);
          return (
            <div key={v.entity_id} className="bg-[#09120D] border border-[#1A2E22] p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-white text-sm font-medium">{v.entity_name}</p>
                  <p className={`text-xs ${trendColors[v.trend]}`}>{v.rotation_rate}x rotation &middot; {v.entity_type}</p>
                </div>
                <span className={`font-['Outfit'] text-xl font-bold ${rotationColor(v.rotation_rate)}`}>{v.rotation_rate}x</span>
              </div>
              <div className="flex items-end gap-2 h-16">
                {v.quarterly.map((q) => (
                  <div key={q.q} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-[#A1B2A8]/40 text-[9px]">{fmtAmt(q.vol)}</span>
                    <div className="w-full bg-[#22C55E]/10 relative" style={{ height: `${Math.max((q.vol / maxVol) * 100, 8)}%` }}>
                      <div className="absolute inset-0 bg-[#22C55E]/50" />
                    </div>
                    <span className="text-[#A1B2A8]/30 text-[9px]">{q.q.replace("20", "")}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
